import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 72;
const DIRECTION_SLOP = 8;
const TOP_TOLERANCE = 2;

/** Only installed, narrow-screen apps own this gesture; browsers keep theirs. */
export function useMobileRefresh(route: string) {
  const [enabled, setEnabled] = useState(false);
  const [distance, setDistance] = useState(0);
  const dirty = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(refreshTimer.current), []);
  const [blocked, setBlocked] = useState(false);
  const [previousRoute, setPreviousRoute] = useState(route);
  if (previousRoute !== route) {
    setPreviousRoute(route);
    setBlocked(false);
    setDistance(0);
  }
  const refresh = () => {
    if (dirty.current || refreshTimer.current !== undefined) return;
    setRefreshing(true);
    refreshTimer.current = setTimeout(() => window.location.reload(), 120);
  };

  useEffect(() => {
    const mobile = matchMedia('(max-width: 48rem)');
    const standalone = matchMedia('(display-mode: standalone)');
    const fullscreen = matchMedia('(display-mode: fullscreen)');
    const update = () => setEnabled(mobile.matches && (standalone.matches || fullscreen.matches ||
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone)));
    update();
    const queries = [mobile, standalone, fullscreen];
    queries.forEach((query) => query.addEventListener('change', update));
    return () => queries.forEach((query) => query.removeEventListener('change', update));
  }, []);

  useEffect(() => {
    dirty.current = false;
    if (!enabled) return;
    let start: { x: number; y: number } | null = null;
    let pull = 0;
    let dragging = false;
    let suppressClickUntil = 0;
    const cancel = () => { start = null; pull = 0; dragging = false; setDistance(0); };
    const edited = () => { dirty.current = true; setBlocked(true); cancel(); };
    const begin = (event: TouchEvent) => {
      cancel();
      if (dirty.current || event.touches.length !== 1 || window.scrollY > TOP_TOLERANCE) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable], [role="dialog"]') ||
          document.activeElement?.matches('input, textarea, select, [contenteditable]')) return;
      // Nested scrollers (such as message history) keep their own gestures.
      for (let node = target; node && node !== document.body; node = node.parentElement) {
        if (node.scrollHeight > node.clientHeight && /auto|scroll/.test(getComputedStyle(node).overflowY)) return;
      }
      start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    };
    const move = (event: TouchEvent) => {
      if (!start) return;
      if (event.touches.length !== 1 || window.scrollY > TOP_TOLERANCE) { cancel(); return; }
      const dx = event.touches[0].clientX - start.x;
      const dy = event.touches[0].clientY - start.y;
      if (!dragging) {
        // Ignore the tiny sideways wobble at the start of a normal swipe.
        if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_SLOP) return;
        if (dy <= 0 || Math.abs(dx) > dy) { cancel(); return; }
        dragging = true;
      }
      pull = Math.min(Math.max(0, dy), 140);
      if (event.cancelable) event.preventDefault();
      setDistance(pull);
    };
    const end = () => {
      const ready = pull >= THRESHOLD && !dirty.current;
      if (dragging) suppressClickUntil = Date.now() + 500;
      cancel();
      if (ready && refreshTimer.current === undefined) {
        setRefreshing(true);
        refreshTimer.current = setTimeout(() => window.location.reload(), 120);
      }
    };
    const suppressDragClick = (event: MouseEvent) => {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('click', suppressDragClick, true);
    document.addEventListener('input', edited, true);
    document.addEventListener('change', edited, true);
    document.addEventListener('touchstart', begin, { passive: true });
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', cancel);
    return () => {
      document.removeEventListener('click', suppressDragClick, true);
      document.removeEventListener('input', edited, true);
      document.removeEventListener('change', edited, true);
      document.removeEventListener('touchstart', begin);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', cancel);
    };
  }, [enabled, route]);
  return { enabled, blocked, refreshing, distance, ready: distance >= THRESHOLD, refresh };
}
